// Utility functions for media URL validation and platform detection

// Supported platforms and their URL patterns
const SUPPORTED_PLATFORMS = {
  YOUTUBE: {
    name: 'YouTube',
    patterns: [
      /^https?:\/\/(www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
      /^https?:\/\/(www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
      /^https?:\/\/youtu\.be\/([a-zA-Z0-9_-]+)/,
      /^https?:\/\/(www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/
    ],
    embedUrl: (id) => `https://www.youtube.com/embed/${id}`
  },
  GOOGLE_DRIVE: {
    name: 'Google Drive',
    patterns: [
      /^https?:\/\/(www\.)?drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
      /^https?:\/\/drive\.google\.com\/uc\?id=([a-zA-Z0-9_-]+)/,
      /^https?:\/\/(www\.)?drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/
    ],
    embedUrl: (id) => `https://drive.google.com/uc?export=view&id=${id}`
  },
  DROPBOX: {
    name: 'Dropbox',
    patterns: [
      /^https?:\/\/(www\.)?dropbox\.com\/s\/([a-zA-Z0-9_-]+)/,
      /^https?:\/\/dl\.dropboxusercontent\.com\/s\/([a-zA-Z0-9_-]+)/
    ],
    embedUrl: (id) => `https://dl.dropboxusercontent.com/s/${id}`
  },
  INSTAGRAM: {
    name: 'Instagram',
    patterns: [
      /^https?:\/\/(www\.)?instagram\.com\/p\/([a-zA-Z0-9_-]+)/,
      /^https?:\/\/(www\.)?instagram\.com\/reel\/([a-zA-Z0-9_-]+)/,
      /^https?:\/\/instagr\.am\/p\/([a-zA-Z0-9_-]+)/
    ],
    embedUrl: (id) => `https://www.instagram.com/p/${id}/embed`
  },
  PINTEREST: {
    name: 'Pinterest',
    patterns: [
      /^https?:\/\/(www\.)?pinterest\.com\/pin\/([0-9]+)/,
      /^https?:\/\/pinterest\.com\/pin\/([0-9]+)/
    ],
    embedUrl: (id) => `https://www.pinterest.com/pin/${id}/embed/`
  },
  TIKTOK: {
    name: 'TikTok',
    patterns: [
      /^https?:\/\/(www\.)?tiktok\.com\/@[\w.-]+\/video\/([0-9]+)/,
      /^https?:\/\/vm\.tiktok\.com\/([a-zA-Z0-9_-]+)/,
      /^https?:\/\/tiktok\.com\/@[\w.-]+\/video\/([0-9]+)/
    ],
    embedUrl: (id) => `https://www.tiktok.com/embed/v2/${id}`
  },
  TWITCH: {
    name: 'Twitch',
    patterns: [
      /^https?:\/\/(www\.)?twitch\.tv\/videos\/([0-9]+)/,
      /^https?:\/\/(www\.)?twitch\.tv\/[\w.-]+\/clip\/([a-zA-Z0-9_-]+)/
    ],
    embedUrl: (id) => `https://player.twitch.tv/?video=${id}&parent=localhost&autoplay=false`
  }
};

/**
 * Detects the platform type from a URL
 * @param {string} url - The URL to analyze
 * @returns {Object|null} - Platform info or null if not supported
 */
export function detectPlatform(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  for (const [platformKey, platform] of Object.entries(SUPPORTED_PLATFORMS)) {
    for (const pattern of platform.patterns) {
      const match = url.match(pattern);
      if (match) {
        return {
          key: platformKey,
          name: platform.name,
          id: match[2] || match[1], // Different regex groups have different positions
          embedUrl: platform.embedUrl(match[2] || match[1])
        };
      }
    }
  }

  return null;
}

/**
 * Validates a media URL
 * @param {string} url - The URL to validate
 * @returns {Object} - Validation result with isValid, error, and platform info
 */
export function validateMediaUrl(url) {
  if (!url || typeof url !== 'string') {
    return {
      isValid: false,
      error: 'URL is required',
      platform: null
    };
  }

  // Basic URL format validation
  try {
    new URL(url);
  } catch (e) {
    return {
      isValid: false,
      error: 'Invalid URL format',
      platform: null
    };
  }

  // Check if URL is from a supported platform
  const platform = detectPlatform(url);
  if (!platform) {
    return {
      isValid: false,
      error: 'Unsupported platform. Please use URLs from YouTube, Google Drive, Dropbox, Instagram, Pinterest, TikTok, or Twitch.',
      platform: null
    };
  }

  return {
    isValid: true,
    error: null,
    platform
  };
}

/**
 * Normalizes a URL for embedding
 * @param {string} url - The original URL
 * @returns {string} - The normalized URL for embedding
 */
export function normalizeUrlForEmbedding(url) {
  const validation = validateMediaUrl(url);
  if (!validation.isValid) {
    return url;
  }

  return validation.platform.embedUrl;
}

/**
 * Gets the platform name from a URL
 * @param {string} url - The URL to analyze
 * @returns {string} - Platform name or 'Unknown'
 */
export function getPlatformName(url) {
  const platform = detectPlatform(url);
  return platform ? platform.name : 'Unknown';
}

/**
 * Gets a list of supported platforms with examples
 * @returns {Array} - List of supported platforms
 */
export function getSupportedPlatforms() {
  return [
    {
      name: 'YouTube',
      examples: [
        'https://www.youtube.com/watch?v=VIDEO_ID',
        'https://youtu.be/VIDEO_ID',
        'https://www.youtube.com/shorts/VIDEO_ID'
      ]
    },
    {
      name: 'Google Drive',
      examples: [
        'https://drive.google.com/file/d/FILE_ID/view',
        'https://drive.google.com/open?id=FILE_ID'
      ]
    },
    {
      name: 'Dropbox',
      examples: [
        'https://www.dropbox.com/s/FILE_ID/FILENAME',
        'https://dl.dropboxusercontent.com/s/FILE_ID/FILENAME'
      ]
    },
    {
      name: 'Instagram',
      examples: [
        'https://www.instagram.com/p/POST_ID/',
        'https://www.instagram.com/reel/REEL_ID/'
      ]
    },
    {
      name: 'Pinterest',
      examples: [
        'https://www.pinterest.com/pin/PIN_ID/'
      ]
    },
    {
      name: 'TikTok',
      examples: [
        'https://www.tiktok.com/@username/video/VIDEO_ID',
        'https://vm.tiktok.com/VIDEO_ID/'
      ]
    },
    {
      name: 'Twitch',
      examples: [
        'https://www.twitch.tv/videos/VIDEO_ID',
        'https://www.twitch.tv/username/clip/CLIP_ID'
      ]
    }
  ];
}